import {
  Button,
  CondensedButton,
  Flex,
  Heading,
  IconAddLine,
  IconAssignmentLine,
  IconDownloadLine,
  IconInfoSolid,
  IconUploadLine,
  ScreenReaderContent,
  Spinner,
  Table,
  Text,
  TextInput,
  Tooltip,
  TruncateText,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { FileType, Files } from "../../../../services/FilesService";
import { TermsContext } from "../../../../utils/Context";
import downloadFile from "../../../../utils/Dom";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import UploadFileModal from "../../Rater/UploadFileModal";
import UploadEvidenceModal from "../UploadEvidenceUpload";
import JALink from "./JALink";

const lastModifiedFilter = {
  limit: 1,
  sortBy: "last_modified",
  sortOrder: "desc",
};

export const JADetails = ({
  planId,
  assessment,
  newGuide,
  setNewGuide,
  handleSetFilesApprover,
  setFileReviewer,
  showTerms,
}) => {
  const { t } = useTranslation();
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const showNotification = useNotification();
  const [guideModal, setGuideModal] = useState(false);
  const [guide, setGuide] = useState(null);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [uploadEvidenceDialog, setUploadEvidenceDialog] = useState(false);
  const [reviewerSpinner, setReviewerSpinner] = useState(false);
  const [assessingTerms, setAssessingTerms] = useState([]);
  const fileReviewerRef = useRef();

  useEffect(() => {
    if (setFileReviewer) {
      if (assessment.evidence_approved_on) {
        setFileReviewer({ current: { value: assessment.evidence_approver } });
      } else {
        setFileReviewer(fileReviewerRef);
      }
    }
  }, []);

  useEffect(() => {
    if (showTerms) {
      const termList = [];
      assessment.assessing_terms.forEach((assessingTermId) => {
        const canvasTerm = canvasTerms.find(
          (term) => term.id === assessingTermId
        );
        const insightsTerm = insightsTerms.find(
          (insightsTerm) => canvasTerm.insights_term_id === insightsTerm.id
        );
        termList.push({ canvasTerm, insightsTerm });
      });
      termList.sort(
        (a, b) =>
          new Date(a.insightsTerm.start_at) - new Date(b.insightsTerm.start_at)
      );
      setAssessingTerms(termList);
    }
  }, [showTerms, assessment, canvasTerms, insightsTerms]);

  useEffect(() => {
    if (assessment?.id) {
      setLoadingGuide(true);
      Files.reviewFiles(
        FileType.ASSESSMENT_GUIDES,
        assessment.id,
        lastModifiedFilter
      ).then((files) => {
        if (files?.length) {
          setGuide(files[0]);
        }
        setLoadingGuide(false);
      });
    }
  }, [assessment]);

  const handleGuideSubmit = async (guideFile) => {
    if (assessment?.id) {
      await Files.saveFile(guideFile, FileType.ASSESSMENT_GUIDES, assessment.id)
        .then(() => {
          Files.reviewFiles(
            FileType.ASSESSMENT_GUIDES,
            assessment.id,
            lastModifiedFilter
          ).then((files) => {
            setGuide(files[0]);
          });
          setGuideModal(false);
          showNotification(t("notifications.guide.uploadSuccess"));
        })
        .catch(() =>
          showNotification(t("notifications.guide.uploadError"), ERROR)
        );
    } else {
      setNewGuide(guideFile);
      setGuideModal(false);
    }
  };

  const handleSubmitFileApprover = async () => {
    setReviewerSpinner(true);
    const result = await handleSetFilesApprover(
      fileReviewerRef.current.value,
      planId,
      assessment.id
    );
    setReviewerSpinner(false);
    if (result) {
      showNotification(t("notifications.evidenceApproval.success"));
    }
  };

  return (
    <>
      <Card style={{ marginRight: "18px" }}>
        <View as="div">
          <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
            {t("guide.assessmentGuide")}
          </Heading>
          <Flex>
            <Flex.Item>
              <Button
                renderIcon={IconUploadLine}
                size="small"
                onClick={() => setGuideModal(true)}
                margin="0 small 0 0"
              >
                {t("guide.uploadGuide")}
              </Button>
            </Flex.Item>
            {loadingGuide ? (
              <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
            ) : (
              <Flex.Item shouldShrink>
                <TruncateText position="middle">
                  {newGuide?.name ??
                    guide?.file_name ??
                    t("guide.noFileSelected")}
                </TruncateText>{" "}
              </Flex.Item>
            )}
            {!newGuide && guide?.file_name && (
              <Flex.Item>
                <Tooltip
                  renderTip={t("guide.downloadAssessmentGuide")}
                  color="primary-inverse"
                  placement="end"
                >
                  <CondensedButton
                    renderIcon={<IconDownloadLine size="x-small" />}
                    size="small"
                    onClick={() =>
                      downloadFile(guide["file_url"], guide["file_name"])
                    }
                  />
                </Tooltip>
              </Flex.Item>
            )}
          </Flex>
        </View>
        {assessment.assessment_type === "external" && (
          <Flex wrap="wrap" gap="small" alignItems="start">
            <Flex.Item shouldGrow>
              <Heading
                as="h3"
                level="h5"
                border="bottom"
                margin="medium 0 small 0"
              >
                {t("assessment.assessmentEvidence")}
              </Heading>
              <Button
                size="small"
                renderIcon={IconAssignmentLine}
                onClick={() => setUploadEvidenceDialog(true)}
              >
                {t("assessment.manageAssessmentEvidence")}
              </Button>
            </Flex.Item>
            <Flex.Item shouldGrow>
              <Heading
                as="h3"
                level="h5"
                border="bottom"
                margin="medium 0 small 0"
              >
                {t("assessment.evidenceApprover")}{" "}
                {!planId && (
                  <Tooltip
                    renderTip={t("assessment.reviewerDisclaimer")}
                    placement="end"
                    on={["click", "hover", "focus"]}
                    color="primary-inverse"
                  >
                    <IconInfoSolid color="brand" />
                  </Tooltip>
                )}
              </Heading>
              {assessment.evidence_approved_on ? (
                <Text size="small">
                  <Text weight="bold" size="small">
                    {assessment.evidence_approver}{" "}
                  </Text>
                  {t("assessment.on")}
                  <Text weight="bold" size="small">
                    {" "}
                    {new Date(
                      assessment.evidence_approved_on + "Z"
                    ).toLocaleString()}
                  </Text>
                </Text>
              ) : (
                <>
                  <TextInput
                    type="email"
                    size="small"
                    placeholder={t("assessment.email")}
                    renderLabel={
                      <ScreenReaderContent>
                        {t("assessment.email")}
                      </ScreenReaderContent>
                    }
                    ref={fileReviewerRef}
                    defaultValue={assessment.evidence_approver}
                    display="inline-block"
                    width="200px"
                    interaction={planId ? "enabled" : "disabled"}
                  />
                  {planId && (
                    <Button
                      size="small"
                      renderIcon={
                        reviewerSpinner ? (
                          <Spinner
                            renderTitle={t("global.pleaseWait")}
                            size="x-small"
                          />
                        ) : (
                          IconAddLine
                        )
                      }
                      disabled={reviewerSpinner}
                      onClick={handleSubmitFileApprover}
                      margin="0 0 0 small"
                    >
                      {t("assessment.set")}
                    </Button>
                  )}
                </>
              )}
            </Flex.Item>
          </Flex>
        )}
        {showTerms && (
          <>
            <Heading
              as="h3"
              level="h5"
              border="bottom"
              margin="medium 0 small 0"
            >
              {t("assessment.termsToBeAssessed")}:{" "}
            </Heading>
            <Table caption={t("assessment.termsToBeAssessed")}>
              <Table.Head>
                <Table.Row>
                  <Table.ColHeader id="term">
                    {t("results.resultsModeByTerm")}
                  </Table.ColHeader>
                  <Table.ColHeader id="link">
                    {t("assessment.link")}{" "}
                    <Tooltip
                      renderTip={
                        <div style={{ maxWidth: 500 }}>
                          {assessment.assessment_type === "external" &&
                          assessment.purl_type !== "juried"
                            ? t("assessment.resendExternalMessage")
                            : t("assessment.resendJAMessage")}{" "}
                          {!assessment.evidence_approved_on &&
                            assessment.evidence_approver &&
                            t("assessment.linkAvailableMessage")}
                        </div>
                      }
                      placement="end"
                      on={["click", "hover", "focus"]}
                      color="primary-inverse"
                    >
                      <IconInfoSolid color="brand" />
                    </Tooltip>
                  </Table.ColHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {assessingTerms.map((term) => (
                  <Table.Row key={term.insightsTerm.id}>
                    <Table.Cell>{term.insightsTerm.name}</Table.Cell>
                    <Table.Cell>
                      <JALink
                        assessment={assessment}
                        planId={planId}
                        term={term}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        )}
      </Card>
      <UploadEvidenceModal
        open={uploadEvidenceDialog}
        planId={planId}
        assessment={assessment}
        handleCancel={() => setUploadEvidenceDialog(false)}
      />
      <UploadFileModal
        shouldOpen={guideModal}
        modalHeaderText={t("guide.uploadGuide")}
        fileDropHeader={t("guide.dropGuide")}
        handleCancel={() => setGuideModal(false)}
        handleSubmit={handleGuideSubmit}
      />
    </>
  );
};
